import React from 'react';
import styled from 'styled-components';
import ButtonBase from './button-base';
import type { LinkVariant } from '../link/link.types';
import forwardRef from '../../private/forwardRef';
import {
    ButtonProps,
    ButtonIconPlacement,
    ButtonSize,
    ButtonVariant,
    ButtonAsLinkVariant,
} from './button.types';

import LinkBase from '../link/link-base';

const ButtonWithLinkVariant = styled(LinkBase).attrs({ as: 'button' })`
    display: inline;
    font-size: inherit;
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
`;

type LabelWithMarginProps = {
    $iconPlacement: ButtonIconPlacement;
    $sizeVariant: ButtonSize;
};

const LabelWithMargin = styled.span<LabelWithMarginProps>`
    ${({ $iconPlacement, theme, $sizeVariant }) =>
        `margin-${$iconPlacement}: ${
            $sizeVariant === 'small'
                ? theme.spacing.twoExtraSmall
                : theme.spacing.extraSmall
        }`};
`;

const IconWrapper = styled.span`
    display: inline-flex;
    vertical-align: text-top;
`;

const defaultProps = {
    variant: 'primary' as ButtonVariant | ButtonAsLinkVariant,
    sizeVariant: 'large' as ButtonSize,
    fullWidth: false,
    compact: false,
    disabled: false,
    hideLabel: false,
    icon: null,
    iconPlacement: 'left' as ButtonIconPlacement,
};

const Button = forwardRef<ButtonProps, 'button'>((props, ref) => {
    const {
        children,
        variant,
        icon,
        iconPlacement,
        hideLabel,
        sizeVariant,
        fullWidth,
        compact,
        dangerouslySetClassNames,
        ...rest
    } = { ...defaultProps, ...props };

    const linkVariantMatches = variant.match(/link-(.*)/);
    // `variant` is validated by the public ButtonProps union; the regex capture
    // re-widens it to string, so narrow it back here.
    const variantName = (
        linkVariantMatches ? linkVariantMatches[1] : variant
    ) as ButtonVariant;

    const renderLabel = () => {
        if (icon) {
            return (
                <LabelWithMargin
                    $iconPlacement={iconPlacement}
                    $sizeVariant={sizeVariant}
                    className={dangerouslySetClassNames?.label}
                >
                    {children}
                </LabelWithMargin>
            );
        }

        return children;
    };

    const renderIcon = () => {
        if (icon) {
            return linkVariantMatches ? (
                <IconWrapper className={dangerouslySetClassNames?.iconWrapper}>
                    {icon}
                </IconWrapper>
            ) : (
                icon
            );
        }
    };

    const commonProps = {
        $variant: variantName,
        $sizeVariant: sizeVariant,
        $fullWidth: fullWidth,
        $compact: compact,
        // aria-label must be a string; a ReactNode child would read as
        // "[object Object]" to assistive tech.
        'aria-label':
            hideLabel && typeof children === 'string' ? children : undefined,
        ref,
        ...rest,
    };

    const renderIconAndLabel = () => (
        <>
            {iconPlacement === 'left' && renderIcon()}
            {!hideLabel ? renderLabel() : null}
            {iconPlacement === 'right' && renderIcon()}
        </>
    );

    return linkVariantMatches ? (
        // In this branch variantName is the captured link-* variant, so it is a
        // LinkVariant; commonProps types it as ButtonVariant for the other branch.
        <ButtonWithLinkVariant
            {...commonProps}
            $variant={variantName as unknown as LinkVariant}
        >
            {renderIconAndLabel()}
        </ButtonWithLinkVariant>
    ) : (
        <ButtonBase {...commonProps}>{renderIconAndLabel()}</ButtonBase>
    );
});

Button.displayName = 'Button';

export default Button;
export { ButtonBase };
